import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { PricePoint } from './HistoricalUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Props {
  routePrices: PricePoint[];
  cssClass?: string;
  hideLegend?: boolean;
  currency?: string;
  showTicks?: boolean;
}

export const CurveMiniChart: React.FC<Props> = ({
  routePrices,
  cssClass = 'w-full h-32',
  hideLegend = false,
  currency = '$',
  showTicks = false,
}) => {
  // Filter visible prices with valid data
  const visiblePrices = routePrices?.filter(({ price }) => !!price) || [];

  if (visiblePrices.length === 0) {
    return <div className="w-full h-20 flex items-center justify-center text-gray-400 text-xs">No curve data</div>;
  }

  const getData = () => {
    const labels = visiblePrices.map((point) => point.key);
    const prices = visiblePrices.map((point) => +(point.price || 0));
    const color = '#19ceb8';

    return {
      labels,
      datasets: [
        {
          label: 'Route Prices',
          data: prices,
          borderColor: color || '#3B82F6',
          pointBackgroundColor: color,
          pointRadius: 4,
          pointHoverRadius: 8,
          fill: false,
          tension: 0.4,
        },
      ],
    };
  };

  const getOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const point = visiblePrices[context.dataIndex];
            return `${point.name}: ${currency} ${context.parsed.y.toLocaleString('en-US', {
              maximumFractionDigits: 0,
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: showTicks,
        grid: {
          display: true,
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          callback: (_value: string, index: number) => visiblePrices[index]?.name,
        },
      },
      y: {
        display: !hideLegend,
        grid: {
          display: showTicks,
        },
      },
    },
    elements: {
      point: {
        hoverRadius: 8,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  });

  return (
    <div className={cssClass}>
      <Line options={getOptions()} data={getData()} plugins={[]} />
    </div>
  );
};
